import { Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import img2xStart from 'img/tooltip/limit-downloads2x.png';
import img2xLimit from 'img/tooltip/mailspace-light2x.png';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { fullTariff, trialTariff } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import { loadPaidInfoRequest } from 'reactApp/modules/paidInfo/paidInfo.module';
import { PaidInfoSelectors } from 'reactApp/modules/paidInfo/paidInfo.selectors';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './TrackDownloadCountPromo.css';

export interface TrackDownloadCountPromoProps {
    targetRef: any;
    downloads: number;
    downloadLimit: number;
    isMobile?: boolean;
    onHide?: () => void;
}

const TOOLTIP_ID = 'tooltipTrackDownload';

const sendAnalytics = (action) => {
    sendDwh({ eventCategory: 'tooltip-limit-sharing', action, dwhData: { place: 'files' } });
};

export const TrackDownloadCountPromo = memo(
    ({ targetRef, downloads, downloadLimit, isMobile, onHide }: TrackDownloadCountPromoProps): any => {
        const dispatch = useDispatch();
        const [show, setShow] = useState(true);
        const isUserWithoutPayment = useSelector(PaidInfoSelectors.isUserWithoutPayment);
        const productId = isUserWithoutPayment ? trialTariff : fullTariff;

        const isAchived = downloads >= downloadLimit;

        useEffect(() => {
            dispatch(loadPaidInfoRequest());
            emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_SHOW);
            sendAnalytics('show');
        }, []);

        const handleClose = useCallback(() => {
            dispatch(UserStorageActions.set(TOOLTIP_ID, true));
            emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_CLOSE);
            sendAnalytics('close');
            setShow(false);
            onHide?.();
        }, []);

        const handleClickBye = useCallback(
            (e) => {
                e.stopPropagation();
                emitAnalyticEvent(AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_CLICK);
                sendAnalytics('click');
                dispatch(
                    loadProductsAndOpenFastCheckout({
                        productId,
                        paySource: 'limit_sharing_files',
                        title: 'Подписка Mail Space: скачивания без лимита и другие опции',
                        hideOtherTariffs: true,
                    })
                );
                setShow(false);
                onHide?.();
            },
            [productId]
        );

        return (
            show && (
                <FloatingTooltip
                    target={targetRef}
                    placement={ETooltipPlacement.bottom}
                    closable
                    closeOnOutsideClick
                    onClose={handleClose}
                    usePortal
                    withinModal
                >
                    <div className={classNames(styles.tooltip, { [styles.tooltipMobile]: isMobile })}>
                        <Title className={classNames(styles.tooltipTitle, { [styles.tooltipTitleMobile]: isMobile })}>
                            {downloads}&nbsp;из&nbsp;{downloadLimit}&nbsp;скачиваний за&nbsp;сегодня
                        </Title>
                        <Spacing size={4} />
                        <Text className={classNames(styles.tooltipText, { [styles.tooltipTextMobile]: isMobile })}>
                            Оформите Mail Space, чтобы получатели могли скачивать файлы без ограничений
                        </Text>
                        <Spacing size={16} />
                        <Button onClick={handleClickBye} sizeMode={ButtonSizeMode.small} theme="vk" className={styles.button}>
                            {isUserWithoutPayment ? 'Снять лимит за 1 ₽' : 'Снять лимит'}
                        </Button>
                        <img
                            src={isAchived ? img2xLimit : img2xStart}
                            alt="Снять лимит на скачивание с пабликов"
                            className={classNames(styles.image, { [styles.imageAchived]: isAchived, [styles.imageMobile]: isMobile })}
                        />
                    </div>
                </FloatingTooltip>
            )
        );
    }
);

TrackDownloadCountPromo.displayName = 'TrackDownloadCountPromo';
